// src/stores/userStore.ts
// ============================================
// 用户状态管理 - 登录/登出/会话持久化/权限管理
//
// 超级管理员：xiaoshe / 手机号 18670323716（密码存放于 Obsidian Vault，禁止硬编码）
// 权限分级：
//   - super_admin（超级管理员）：可查看 + 可修改设置 + 可管理员工账户
//   - employee（普通员工）：可查看 + 可添加自己的工作计划，不能修改设置
//
// 密码规则：大小写字母 + 数字，至少 8 位
// ============================================

import { create } from "zustand";

// 用户角色
// v6.3.0 新增 guest（游客）：无需密码进入系统，仅可体验 AI 超市等只读/生成功能，
// 不可修改设置/管理员工/写敏感配置（super_admin 专属操作对游客隐藏）
export type UserRole = "super_admin" | "employee" | "guest";

// 用户信息
// v6.x 权限体系扩展：role 为主角色；employee 下细分 sub_role（content/sales），
// 超管可选 bossMode（老板只读/决策优先视图）。
export interface UserInfo {
  username: string;
  displayName: string;
  phone: string;
  role: UserRole;
  /** 员工细分：content（内容专员）/ sales（销售专员）；超管为空 */
  sub_role?: "content" | "sales" | "";
  /** 超管是否启用老板只读模式视图 */
  bossMode?: boolean;
  loginAt: string;
}

// 登录凭据
interface LoginCredentials {
  username: string;
  password: string;
}

// ============================================
// 审计日志 - 持久化到 Obsidian Vault XEAOS/Audit/
// 白皮书铁律一 + 用户偏好：real AUDIT LOGS（非模拟/硬编码）
// 登录/登出/注册等关键操作写入 Vault，重启后可查
// ============================================

export interface AuditEntry {
  /** 操作类型 */
  action: "login" | "logout" | "register" | "change_password" | "login_failed";
  /** 用户名 */
  username: string;
  /** 是否成功 */
  success: boolean;
  /** 角色 */
  role?: string;
  /** 失败原因 */
  error?: string;
  /** 时间戳 */
  timestamp: string;
}

/**
 * 写入审计日志到 Vault XEAOS/Audit/LoginLog-{date}.md
 * 异步执行，失败不阻塞主流程（仅 console.warn）
 */
async function writeAuditLog(entry: AuditEntry): Promise<void> {
  try {
    // v6.22 由服务端记录：/audit-log 判定真实 IP（防伪造）+ 归属地 + UA + 写 Vault
    const resp = await fetch('/audit-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: entry.action,
        username: entry.username,
        success: entry.success,
        role: entry.role || '',
        error: entry.error || '',
        timestamp: entry.timestamp,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    }).catch(() => null);
    if (resp && resp.ok) {
      return;
    }
    // 降级：服务端不可用时回退本地写入（无 IP 信息）
    const date = entry.timestamp.slice(0, 10); // YYYY-MM-DD
    const time = entry.timestamp.slice(11, 19); // HH:MM:SS
    const relPath = `XEAOS/Audit/LoginLog-${date}.md`;

    // 读取现有内容（追加模式）
    let existingContent = "";
    try {
      const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
      if (readResp.ok) {
        const readData = await readResp.json();
        if (readData?.success && typeof readData.content === "string") {
          existingContent = readData.content;
        }
      }
    } catch {
      // 文件不存在，正常情况
    }

    // 构建新行
    const resultMark = entry.success ? "✅" : "❌";
    const roleStr = entry.role || "-";
    const errorStr = entry.error ? ` · ${entry.error}` : "";
    const newRow = `| ${time} | ${entry.username} | ${entry.action} | ${resultMark} ${entry.success ? "成功" : "失败"}${errorStr} | ${roleStr} | - | - |`;

    let newContent: string;
    if (existingContent) {
      // 追加到表格末尾（找最后一行 | 开头的位置）
      const lines = existingContent.split("\n");
      let lastTableRowIdx = -1;
      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim().startsWith("|")) {
          lastTableRowIdx = i;
          break;
        }
      }
      if (lastTableRowIdx >= 0) {
        lines.splice(lastTableRowIdx + 1, 0, newRow);
        newContent = lines.join("\n");
      } else {
        // 表格不存在，追加新表格
        newContent = existingContent + "\n\n" + buildAuditTableHeader(date) + "\n" + newRow;
      }
    } else {
      // 新建文件
      newContent = buildAuditTableHeader(date) + "\n" + newRow + "\n";
    }

    // 写回 Vault
    await fetch(`/vault-api/write?path=${encodeURIComponent(relPath)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: newContent }),
    });
  } catch (err) {
    // 审计日志失败不阻塞主流程
    console.warn("[userStore] 审计日志写入失败:", err);
  }
}

/** 构建审计日志文件头（frontmatter + 表头） */
function buildAuditTableHeader(date: string): string {
  return [
    "---",
    "type: audit_log",
    `date: ${date}`,
    "module: auth",
    "---",
    "",
    `# 登录审计日志 ${date}`,
    "",
    "白皮书铁律一：真实审计日志，记录所有登录/登出/注册操作。",
    "",
    "| 时间 | 用户 | 操作 | 结果 | 角色 | IP | UserAgent |",
    "|------|------|------|------|------|----|-----------|",
  ].join("\n");
}

/**
 * 查询审计日志（供企业中心展示）
 * @param date YYYY-MM-DD，默认今天
 */
export async function queryAuditLog(date?: string): Promise<{ success: boolean; content: string; entries: AuditEntry[] }> {
  try {
    const targetDate = date || new Date().toISOString().slice(0, 10);
    const relPath = `XEAOS/Audit/LoginLog-${targetDate}.md`;
    const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
    if (!readResp.ok) {
      return { success: false, content: "", entries: [] };
    }
    const readData = await readResp.json();
    if (!readData?.success || typeof readData.content !== "string") {
      return { success: false, content: "", entries: [] };
    }
    // 解析表格行为 AuditEntry
    const entries: AuditEntry[] = [];
    const lines = readData.content.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("|") || trimmed.includes("---") || trimmed.includes("时间")) continue;
      const cells = trimmed.split("|").map((c: string) => c.trim()).filter((x: string) => x.length > 0);
      if (cells.length < 4) continue;
      // | 时间 | 用户 | 操作 | 结果 | 角色 | IP | UserAgent |
      const [time, username, action, result, role] = cells;
      const success = result.includes("✅");
      entries.push({
        action: action as AuditEntry["action"],
        username,
        success,
        role: role === "-" ? undefined : role,
        timestamp: `${targetDate}T${time}`,
        error: success ? undefined : result.split("·")[1]?.trim(),
      });
    }
    return { success: true, content: readData.content, entries };
  } catch (err) {
    console.warn("[userStore] 查询审计日志失败:", err);
    return { success: false, content: "", entries: [] };
  }
}

/** 查询最近 N 天的审计日志 */
export async function queryRecentAuditLogs(days: number = 7): Promise<{ success: boolean; logs: Array<{ date: string; entries: AuditEntry[] }> }> {
  const logs: Array<{ date: string; entries: AuditEntry[] }> = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const result = await queryAuditLog(dateStr);
    if (result.success && result.entries.length > 0) {
      logs.push({ date: dateStr, entries: result.entries });
    }
  }
  return { success: true, logs };
}

// 超级管理员账号配置 —— 白皮书铁律一：从 Obsidian Vault 读取，禁止硬编码密码凭据
// 密码不再出现在代码中；首次运行自动生成强随机密码并写入 Vault：
//   XEAOS/Config/AdminConfig.md
// 密码可通过 changePassword 修改（同步写回 Vault + localStorage 缓存）
const SUPER_ADMIN_KEY = "xeaos_super_admin_v2";
const SUPER_ADMIN_VAULT_PATH = "XEAOS/Config/AdminConfig.md";

/** 超级管理员配置（含明文密码，仅存于 Vault，绝不进入代码/构建产物） */
interface SuperAdminConfig {
  username: string;
  password: string;
  phone: string;
  displayName: string;
}

/** 默认身份信息（不含密码 —— 密码由 Vault 提供或首次运行生成） */
const DEFAULT_SUPER_ADMIN_IDENTITY = {
  username: "xiaoshe",
  phone: "18670323716",
  displayName: "小舍科技·超级管理员",
};

/** 内存缓存（避免每次登录重复读 Vault） */
let _superAdminCache: SuperAdminConfig | null = null;

/** 是否本次会话内自动生成了新密码（供登录页提示） */
let _generatedPasswordThisSession = false;

/** 生成强随机密码（14 位，含大小写+数字+符号） */
function generateStrongPassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + digits + symbols;
  const rand = (n: number) => Math.floor(Math.random() * n);
  const parts = [
    upper[rand(upper.length)],
    lower[rand(lower.length)],
    digits[rand(digits.length)],
    symbols[rand(symbols.length)],
  ];
  for (let i = 0; i < 10; i++) parts.push(all[rand(all.length)]);
  for (let i = parts.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }
  return parts.join("");
}

/** 解析 Vault AdminConfig.md（简化 YAML frontmatter） */
function parseAdminConfig(content: string): SuperAdminConfig | null {
  try {
    const fm: Record<string, string> = {};
    const trimmed = content.trimStart();
    if (trimmed.startsWith("---")) {
      const endIndex = trimmed.indexOf("\n---", 3);
      const yamlBlock = endIndex === -1 ? trimmed.slice(3) : trimmed.slice(3, endIndex);
      for (const line of yamlBlock.split("\n")) {
        const t = line.trim();
        if (!t || t.startsWith("#") || t.startsWith("-")) continue;
        const idx = t.indexOf(":");
        if (idx === -1) continue;
        fm[t.slice(0, idx).trim()] = t.slice(idx + 1).trim().replace(/^["']|["']$/g, "");
      }
    }
    if (!fm.username || !fm.password) return null;
    return {
      username: fm.username,
      password: fm.password,
      phone: fm.phone || DEFAULT_SUPER_ADMIN_IDENTITY.phone,
      displayName: fm.displayName || DEFAULT_SUPER_ADMIN_IDENTITY.displayName,
    };
  } catch {
    return null;
  }
}

/** 序列化管理员配置为 Markdown */
function serializeAdminConfig(cfg: SuperAdminConfig): string {
  return [
    "---",
    "type: admin_config",
    `username: ${cfg.username}`,
    `password: ${cfg.password}`,
    `phone: ${cfg.phone}`,
    `displayName: "${cfg.displayName}"`,
    `updated_at: ${new Date().toISOString()}`,
    "---",
    "",
    "# XEAOS 超级管理员配置",
    "",
    "> ⚠️ 本文件包含管理员登录密码，请妥善保管（白皮书铁律一：凭据存放于知识库）。",
    `> 登录用户名：${cfg.username}`,
    `> 登录密码：${cfg.password}`,
  ].join("\n");
}

/** 从 Vault 读取超级管理员配置 */
async function readSuperAdminFromVault(): Promise<SuperAdminConfig | null> {
  try {
    const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(SUPER_ADMIN_VAULT_PATH)}`);
    if (!resp.ok) return null;
    const json = await resp.json();
    if (json?.success && typeof json.content === "string") {
      return parseAdminConfig(json.content);
    }
  } catch {
    // vault-api 不可用时忽略
  }
  return null;
}

/** 写入超级管理员配置到 Vault */
async function writeSuperAdminToVault(cfg: SuperAdminConfig): Promise<boolean> {
  try {
    const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(SUPER_ADMIN_VAULT_PATH)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: serializeAdminConfig(cfg) }),
    });
    if (resp.ok) {
      const json = await resp.json();
      return json?.success === true;
    }
  } catch {
    // 忽略
  }
  return false;
}

/**
 * 确保超级管理员配置可用（优先级）：
 *   1. 内存缓存
 *   2. Vault XEAOS/Config/AdminConfig.md（唯一真相源）
 *   3. localStorage 缓存（兼容旧版改密持久化）
 *   4. 生成强随机密码并写入 Vault（凭据在知识库，不在代码）
 */
async function ensureSuperAdmin(): Promise<SuperAdminConfig> {
  if (_superAdminCache) return _superAdminCache;

  const fromVault = await readSuperAdminFromVault();
  if (fromVault) {
    _superAdminCache = fromVault;
    return fromVault;
  }

  try {
    const stored = localStorage.getItem(SUPER_ADMIN_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.username && parsed?.password) {
        const cfg: SuperAdminConfig = { ...DEFAULT_SUPER_ADMIN_IDENTITY, ...parsed };
        _superAdminCache = cfg;
        return cfg;
      }
    }
  } catch {
    // ignore
  }

  const generated: SuperAdminConfig = {
    ...DEFAULT_SUPER_ADMIN_IDENTITY,
    password: generateStrongPassword(),
  };
  const written = await writeSuperAdminToVault(generated);
  if (written) {
    _generatedPasswordThisSession = true;
    _superAdminCache = generated;
    return generated;
  }
  // Vault 不可写：退回 localStorage 并标记（仅离线兜底）
  try {
    localStorage.setItem(SUPER_ADMIN_KEY, JSON.stringify(generated));
  } catch {
    // ignore
  }
  _generatedPasswordThisSession = true;
  _superAdminCache = generated;
  return generated;
}

/** 保存超级管理员配置（Vault + localStorage 双写） */
async function saveSuperAdmin(config: SuperAdminConfig): Promise<void> {
  try {
    await writeSuperAdminToVault(config);
  } catch {
    // 忽略
  }
  try {
    localStorage.setItem(SUPER_ADMIN_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
  _superAdminCache = config;
}

/** 是否本次会话内自动生成了新密码（供登录页展示首次运行提示） */
function wasPasswordGeneratedThisSession(): boolean {
  return _generatedPasswordThisSession;
}

/**
 * 规范化输入字符串，处理常见的输入法问题
 * 1. 去除首尾空格
 * 2. 全角字符转半角（中文输入法下可能输入全角字符）
 * 3. 去除零宽字符
 * 4. 去除 BOM 标记
 */
function normalizeInput(input: string): string {
  if (!input) return '';
  let result = input
    // 去除 BOM 标记
    .replace(/\uFEFF/g, '')
    // 全角字母数字转半角（FF01-FF5E → 0021-007E）
    .replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    // 全角空格转半角
    .replace(/\u3000/g, ' ')
    // 去除零宽字符（零宽空格、零宽连字符、零宽非连字符）
    .replace(/[\u200B-\u200D\u2060]/g, '')
    // 去除首尾空格
    .trim();
  return result;
}

/**
 * 密码强度验证
 * 规则：必须包含大小写字母和数字，至少 8 位
 */
export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (!password || password.length < 8) {
    return { valid: false, error: "密码至少 8 位" };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, error: "密码必须包含小写字母" };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: "密码必须包含大写字母" };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, error: "密码必须包含数字" };
  }
  return { valid: true };
}

// 普通员工账号（v6.22 由 localStorage 升级为 Vault 存储：员工账号需跨设备登录，
// localStorage 只在管理员本机可见 → 员工在其他设备登录报"用户名密码错误"）
// 唯一真相源：Vault XEAOS/Config/Employees.md（JSON 数组）；localStorage 仅作兼容兜底
const EMPLOYEES_KEY = "xeaos_employees_v2";
const EMPLOYEES_VAULT_PATH = "XEAOS/Config/Employees.md";
let _employeesCache: EmployeeRecord[] | null = null;

export interface EmployeeRecord {
  username: string;
  password: string;
  phone: string;
  displayName: string;
  /** 职位细分：content（内容专员）/ sales（销售专员），用于菜单与接口权限裁剪 */
  sub_role?: "content" | "sales" | "";
  createdAt: string;
}

// 会话存储 key
// v2: 同步更新 key 名称，清除旧会话
const SESSION_KEY = "xeaos_session_v2";

// 服务端访问令牌存储键（由 /api/login 签发，vaultAccessGuard 附加到请求头 X-XEAOS-Token）
const TOKEN_STORAGE_KEY = "xeaos_token_v2";

// v6.22 从 Vault 读取员工列表（跨设备唯一真相源）
async function loadEmployeesFromVault(): Promise<EmployeeRecord[] | null> {
  try {
    const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(EMPLOYEES_VAULT_PATH)}`).catch(() => null);
    if (resp && resp.ok) {
      const result = await resp.json();
      if (result.success && result.content) {
        const raw = String(result.content).replace(/^---\n[\s\S]*?\n---\n/, '').trim();
        // 兼容纯 JSON 或 JSON 代码块
        const jsonText = raw.replace(/^```json\n?|```$/g, '').trim();
        const parsed = JSON.parse(jsonText);
        if (Array.isArray(parsed)) return parsed as EmployeeRecord[];
      }
    }
  } catch (err) {
    console.warn('[userStore] 员工列表 Vault 读取失败，回退 localStorage:', err);
  }
  return null;
}

// v6.22 写入 Vault（跨设备同步）
async function saveEmployeesToVault(employees: EmployeeRecord[]): Promise<boolean> {
  try {
    const content = `---\ntype: employees\npurpose: XEAOS 员工账号（跨设备登录唯一真相源）\nupdated_at: ${new Date().toISOString()}\n---\n\n\`\`\`json\n${JSON.stringify(employees, null, 2)}\n\`\`\`\n`;
    const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(EMPLOYEES_VAULT_PATH)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    }).catch(() => null);
    return !!(resp && resp.ok);
  } catch (err) {
    console.warn('[userStore] 员工列表 Vault 写入失败:', err);
    return false;
  }
}

// v6.22 确保员工列表已加载（Vault 优先 → localStorage 兜底）
async function ensureEmployeesLoaded(): Promise<EmployeeRecord[]> {
  if (_employeesCache) return _employeesCache;
  const fromVault = await loadEmployeesFromVault();
  if (fromVault) {
    _employeesCache = fromVault;
    try {
      localStorage.setItem(EMPLOYEES_KEY, JSON.stringify(fromVault));
    } catch { /* ignore */ }
    return fromVault;
  }
  // Vault 无（首次迁移前）：回退 localStorage 并回写 Vault
  const legacy = loadEmployeesFromLocal();
  _employeesCache = legacy;
  if (legacy.length > 0) {
    void saveEmployeesToVault(legacy);
  }
  return legacy;
}

// 读取员工列表（同步：缓存优先，其次 localStorage——兼容旧逻辑与未 hydrate 场景）
function loadEmployees(): EmployeeRecord[] {
  if (_employeesCache) return _employeesCache;
  return loadEmployeesFromLocal();
}

function loadEmployeesFromLocal(): EmployeeRecord[] {
  try {
    const stored = localStorage.getItem(EMPLOYEES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return [];
}

// 保存员工列表（缓存 + localStorage + 异步写 Vault——跨设备生效）
function saveEmployees(employees: EmployeeRecord[]) {
  _employeesCache = employees;
  try {
    localStorage.setItem(EMPLOYEES_KEY, JSON.stringify(employees));
  } catch {
    // ignore
  }
  void saveEmployeesToVault(employees);
}

// ============================================
// Store 定义
// ============================================

interface UserState {
  /** 当前登录用户（null 表示未登录） */
  currentUser: UserInfo | null;
  /** 是否已完成首次检查（避免刷新时闪烁登录页） */
  hydrated: boolean;
  /** 本次会话内是否自动生成了管理员初始密码（首次运行提示） */
  adminPasswordGenerated: boolean;

  /** 从 localStorage 恢复会话 + 确保管理员配置就绪（从 Vault 读取/生成） */
  hydrate: () => Promise<void>;
  /** 登录（支持用户名或手机号） */
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
  /** v6.3.0 游客登录：免密进入系统，体验 AI 超市等只读/生成功能 */
  loginAsGuest: () => { success: boolean };
  /** 登出 */
  logout: () => void;
  /** 修改自己的密码 */
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  /** 超级管理员修改员工密码 */
  changeEmployeePassword: (username: string, newPassword: string) => { success: boolean; error?: string };
  /** 添加普通员工（仅超级管理员可调用） */
  addEmployee: (employee: Omit<EmployeeRecord, 'createdAt'>) => { success: boolean; error?: string };
  /** 列出所有员工 */
  listEmployees: () => EmployeeRecord[];
  /** 删除员工 */
  removeEmployee: (username: string) => { success: boolean; error?: string };
  /** 检查是否为超级管理员 */
  isSuperAdmin: () => boolean;
  /** 检查是否可修改设置 */
  canModifySettings: () => boolean;
  /** 重置超级管理员密码为新随机密码（忘记密码时使用，写回 Vault） */
  resetSuperAdminPassword: () => Promise<{ success: boolean; password: string }>;
}

export const useUserStore = create<UserState>((set, get) => ({
  currentUser: null,
  hydrated: false,
  adminPasswordGenerated: false,

  hydrate: async () => {
    try {
      // 确保管理员配置就绪：优先 Vault XEAOS/Config/AdminConfig.md，首次运行自动生成随机密码落盘
      const admin = await ensureSuperAdmin();
      if (wasPasswordGeneratedThisSession()) {
        set({ adminPasswordGenerated: true });
      }
      void admin; // 仅预热缓存

      // v6.22 预热员工列表（Vault 优先，跨设备登录依赖）
      void ensureEmployeesLoaded();

      // 清除旧版本会话数据（v1），避免恢复旧的错误会话
      localStorage.removeItem('xeaos_session');

      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const user = JSON.parse(stored) as UserInfo;
        set({ currentUser: user, hydrated: true });
        return;
      }
    } catch {
      // ignore
    }
    set({ hydrated: true });
  },

  // v6.3.0 游客登录：无需密码进入系统，仅体验 AI 超市等只读/生成功能
  // 权限边界：游客 role="guest"，super_admin 专属操作（设置修改/员工管理/写敏感配置）对其隐藏；
  // 游客会话不写入审计日志（不是真实用户），刷新后需重新进入
  loginAsGuest: () => {
    const user: UserInfo = {
      username: "guest",
      displayName: "游客体验",
      phone: "",
      role: "guest",
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
    set({ currentUser: user });
    return { success: true };
  },

  login: async (credentials) => {
    // 规范化输入：去除首尾空格、全角转半角、去零宽字符、去 BOM
    const username = normalizeInput(credentials.username);
    const password = normalizeInput(credentials.password);

    // 安全日志：仅记录用户名长度和密码长度，禁止输出明文密码或字符码
    console.log('[userStore] 登录请求:', {
      username,
      usernameLength: username.length,
      passwordLength: password.length,
    });

    // v6.x P0：登录鉴权迁移到服务端（/api/login 校验 Vault 凭据 + 签发令牌 + 防暴力破解）。
    // 前端不再读取/比对明文密码，避免把凭据文件拉回浏览器；令牌单独存储，由 vaultAccessGuard 附加。
    let resp: Response;
    try {
      resp = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
    } catch {
      return { success: false, error: '无法连接服务端，请稍后再试' };
    }

    let data: any = {};
    try {
      data = await resp.json();
    } catch {
      /* 忽略非 JSON 响应 */
    }

    if (resp.status === 429) {
      return { success: false, error: data.error || '登录失败次数过多，账号已锁定，请稍后再试' };
    }
    if (!resp.ok || !data.success) {
      void writeAuditLog({
        action: "login_failed",
        username,
        success: false,
        error: data.error || "用户名或密码错误",
        timestamp: new Date().toISOString(),
      });
      return { success: false, error: data.error || "用户名或密码错误" };
    }

    // 成功：保存令牌 + 会话信息（角色/细分/老板模式由服务端下发）
    localStorage.setItem(TOKEN_STORAGE_KEY, data.token || '');
    const user: UserInfo = {
      username: data.username,
      displayName: data.displayName || data.username,
      phone: data.phone || '',
      role: data.role || "guest",
      sub_role: data.sub_role || "",
      bossMode: !!data.bossMode,
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
    set({ currentUser: user, adminPasswordGenerated: false });
    console.log('[userStore] 登录成功:', user.username, '(' + user.role + ')');
    // 白皮书铁律一：登录成败审计由服务端 /api/login 落库（含真实 IP），此处保留本地日志
    void writeAuditLog({
      action: "login",
      username: user.username,
      success: true,
      role: user.role,
      timestamp: user.loginAt,
    });
    return { success: true };
  },

  logout: () => {
    const current = get().currentUser;
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    set({ currentUser: null });
    console.log('[userStore] 用户已登出');
    // 白皮书铁律一：登出也记录审计日志
    if (current) {
      void writeAuditLog({
        action: "logout",
        username: current.username,
        success: true,
        role: current.role,
        timestamp: new Date().toISOString(),
      });
    }
  },

  changePassword: async (oldPassword, newPassword) => {
    const current = get().currentUser;
    if (!current) {
      return { success: false, error: "未登录" };
    }

    const normalizedOld = normalizeInput(oldPassword);
    const normalizedNew = normalizeInput(newPassword);

    // 验证新密码强度
    const validation = validatePassword(normalizedNew);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    if (current.role === "super_admin") {
      // 超级管理员修改密码（严格区分大小写；写回 Vault XEAOS/Config/AdminConfig.md）
      const superAdmin = await ensureSuperAdmin();
      if (normalizedOld !== superAdmin.password) {
        return { success: false, error: "原密码错误" };
      }
      const updated = { ...superAdmin, password: normalizedNew };
      await saveSuperAdmin(updated);
      console.log('[userStore] 超级管理员密码已修改（已写回 Vault）');
      return { success: true };
    } else {
      // 普通员工修改密码（严格区分大小写）
      const employees = loadEmployees();
      const employee = employees.find((e) => e.username === current.username);
      if (!employee) {
        return { success: false, error: "员工账户不存在" };
      }
      if (normalizedOld !== employee.password) {
        return { success: false, error: "原密码错误" };
      }
      employee.password = normalizedNew;
      saveEmployees(employees);
      console.log('[userStore] 员工密码已修改:', current.username);
      return { success: true };
    }
  },

  changeEmployeePassword: (username, newPassword) => {
    const current = get().currentUser;
    if (!current || current.role !== "super_admin") {
      return { success: false, error: "仅超级管理员可修改员工密码" };
    }

    const normalizedUsername = normalizeInput(username);
    const normalizedNew = normalizeInput(newPassword);

    // 验证新密码强度
    const validation = validatePassword(normalizedNew);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const employees = loadEmployees();
    const employee = employees.find((e) => e.username === normalizedUsername);
    if (!employee) {
      return { success: false, error: "员工不存在" };
    }
    employee.password = normalizedNew;
    saveEmployees(employees);
    console.log('[userStore] 超级管理员已修改员工密码:', normalizedUsername);
    return { success: true };
  },

  addEmployee: (employee) => {
    const current = get().currentUser;
    if (!current || current.role !== "super_admin") {
      return { success: false, error: "仅超级管理员可添加员工" };
    }

    const normalizedUsername = normalizeInput(employee.username);
    const normalizedPassword = normalizeInput(employee.password);

    // 验证密码强度
    const validation = validatePassword(normalizedPassword);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const employees = loadEmployees();
    if (employees.find((e) => e.username === normalizedUsername)) {
      return { success: false, error: "用户名已存在" };
    }
    if (employees.find((e) => e.phone === employee.phone)) {
      return { success: false, error: "手机号已存在" };
    }

    employees.push({
      username: normalizedUsername,
      password: normalizedPassword,
      phone: employee.phone,
      displayName: employee.displayName,
      createdAt: new Date().toISOString(),
    });
    saveEmployees(employees);
    console.log('[userStore] 员工已添加:', normalizedUsername);
    return { success: true };
  },

  listEmployees: () => loadEmployees(),

  removeEmployee: (username) => {
    const current = get().currentUser;
    if (!current || current.role !== "super_admin") {
      return { success: false, error: "仅超级管理员可删除员工" };
    }
    const employees = loadEmployees().filter((e) => e.username !== username);
    saveEmployees(employees);
    console.log('[userStore] 员工已删除:', username);
    return { success: true };
  },

  isSuperAdmin: () => get().currentUser?.role === "super_admin",

  canModifySettings: () => get().currentUser?.role === "super_admin",

  resetSuperAdminPassword: async () => {
    try {
      // 生成新随机密码并写回 Vault（白皮书铁律一：凭据在知识库，不在代码）
      const current = await ensureSuperAdmin();
      const newPassword = generateStrongPassword();
      const updated = { ...current, password: newPassword };
      await saveSuperAdmin(updated);
      _generatedPasswordThisSession = true;
      console.log('[userStore] 超级管理员密码已重置为新随机密码（已写回 Vault）');
      return { success: true, password: newPassword };
    } catch (err) {
      console.error('[userStore] 重置密码失败:', err);
      return { success: false, password: "" };
    }
  },
}));
